import { useState, FormEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocalizedPath } from "../hooks/useLocalizedPath";
import { SEOMeta } from "../components/SEOMeta";
import emailjs from "@emailjs/browser";
import { Section, Container } from "../components/ui";
import { RelatedPages } from "../components/RelatedPages";
import { trackEvent, identifyLead } from "../lib/analytics";
import styles from "./Contact.module.css";

interface FormData {
  company: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  website: string;
  message: string;
}

export function Contact() {
  const { t } = useTranslation("contact");
  const { lp } = useLocalizedPath();
  const [formData, setFormData] = useState<FormData>({
    company: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    website: "",
    message: "",
  });
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus("sending");

    try {
      await emailjs.send(
        import.meta.env.VITE_EMAILJS_SERVICE_ID,
        import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
        {
          company: formData.company,
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.phone || t("form.notProvided"),
          website: formData.website || t("form.notProvided"),
          message: formData.message,
        },
        import.meta.env.VITE_EMAILJS_PUBLIC_KEY,
      );
      identifyLead({
        email: formData.email,
        company: formData.company,
        name: `${formData.firstName} ${formData.lastName}`,
        phone: formData.phone,
      });
      trackEvent("lead_contact_submitted", { source: "contact_page", company: formData.company });
      setStatus("sent");
      setFormData({ company: "", firstName: "", lastName: "", email: "", phone: "", website: "", message: "" });
    } catch (err) {
      console.error("[Contact] EmailJS error:", err);
      console.error("[Contact] ENV check:", {
        serviceId: !!import.meta.env.VITE_EMAILJS_SERVICE_ID,
        templateId: !!import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
        publicKey: !!import.meta.env.VITE_EMAILJS_PUBLIC_KEY,
      });
      setStatus("error");
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <>
      <SEOMeta title={t("meta.title")} description={t("meta.description")} />

      <Section variant="dark" className={styles.hero}>
        <Container>
          <div className={styles.heroContent}>
            <h1>{t("hero.title")}</h1>
            <p className={styles.heroSubtitle}>{t("hero.subtitle")}</p>
          </div>
        </Container>
      </Section>

      <Section variant="light">
        <Container>
          <div className={styles.grid}>
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <h2 className={styles.cardTitle}>{t("form.title")}</h2>
                <p className={styles.cardDescription}>{t("form.description")}</p>
              </div>
              <div className={styles.cardContent}>
                <form className={styles.form} onSubmit={handleSubmit}>
                  <div className={styles.field}>
                    <label htmlFor="company" className={styles.label}>{t("form.company")}</label>
                    <input type="text" id="company" name="company" required placeholder={t("form.companyPlaceholder")} value={formData.company} onChange={handleChange} className={styles.input} />
                  </div>

                  <div className={styles.row}>
                    <div className={styles.field}>
                      <label htmlFor="firstName" className={styles.label}>{t("form.firstName")}</label>
                      <input type="text" id="firstName" name="firstName" required placeholder={t("form.firstNamePlaceholder")} value={formData.firstName} onChange={handleChange} className={styles.input} />
                    </div>
                    <div className={styles.field}>
                      <label htmlFor="lastName" className={styles.label}>{t("form.lastName")}</label>
                      <input type="text" id="lastName" name="lastName" required placeholder={t("form.lastNamePlaceholder")} value={formData.lastName} onChange={handleChange} className={styles.input} />
                    </div>
                  </div>

                  <div className={styles.row}>
                    <div className={styles.field}>
                      <label htmlFor="email" className={styles.label}>{t("form.email")}</label>
                      <input type="email" id="email" name="email" required placeholder={t("form.emailPlaceholder")} value={formData.email} onChange={handleChange} className={styles.input} />
                    </div>
                    <div className={styles.field}>
                      <label htmlFor="phone" className={styles.label}>{t("form.phone")}</label>
                      <input type="tel" id="phone" name="phone" placeholder={t("form.phonePlaceholder")} value={formData.phone} onChange={handleChange} className={styles.input} />
                    </div>
                  </div>

                  <div className={styles.field}>
                    <label htmlFor="website" className={styles.label}>{t("form.website")}</label>
                    <input type="text" id="website" name="website" placeholder={t("form.websitePlaceholder")} value={formData.website} onChange={handleChange} className={styles.input} />
                  </div>

                  <div className={styles.field}>
                    <label htmlFor="message" className={styles.label}>{t("form.message")}</label>
                    <textarea id="message" name="message" required rows={4} placeholder={t("form.messagePlaceholder")} value={formData.message} onChange={handleChange} className={styles.textarea} />
                  </div>

                  <button type="submit" className={styles.submitButton} disabled={status === "sending"}>
                    {status === "sending" ? (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={styles.spinner}>
                          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                        </svg>
                        {t("form.sending")}
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
                          <path d="m21.854 2.147-10.94 10.939" />
                        </svg>
                        {t("form.submit")}
                      </>
                    )}
                  </button>

                  {status === "sent" && (
                    <div className={styles.successMessage}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                      {t("form.success")}
                    </div>
                  )}

                  {status === "error" && (
                    <div className={styles.errorMessage}>
                      {t("form.error")}{" "}
                      <a href="mailto:partners@qbp.solutions">partners@qbp.solutions</a>
                    </div>
                  )}

                  <p className={styles.legalNote}>
                    {t("form.legalNote")}{" "}
                    <Link to={lp("/mentions-legales")}>{t("form.legalLink")}</Link>.
                  </p>
                </form>
              </div>
            </div>

            <div className={styles.sidebar}>
              <div className={styles.sidebarImage}>
                <img src="/images/contact-hero.webp" alt={t("sidebar.imageAlt")} width={1920} height={1424} loading="lazy" />
              </div>
              <div className={styles.infoCard}>
                <div className={styles.infoCardHeader}>
                  <h3 className={styles.infoCardTitle}>{t("sidebar.contactTitle")}</h3>
                </div>
                <div className={styles.infoCardContent}>
                  <div className={styles.infoItem}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.infoIcon} aria-hidden="true">
                      <path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384" />
                    </svg>
                    <div>
                      <div className={styles.infoLabel}>{t("sidebar.phoneLabel")}</div>
                      <a href="tel:0279655611" className={styles.infoValue} data-attr="contact-phone">02 79 65 56 11</a>
                    </div>
                  </div>
                  <div className={styles.infoItem}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.infoIcon} aria-hidden="true">
                      <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" />
                      <rect x="2" y="4" width="20" height="16" rx="2" />
                    </svg>
                    <div>
                      <div className={styles.infoLabel}>{t("sidebar.emailLabel")}</div>
                      <a href="mailto:partners@qbp.solutions" className={styles.infoValue} data-attr="contact-email">partners@qbp.solutions</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      <RelatedPages currentFrPath="/contact" />
    </>
  );
}
